import {
  CognitiveMode,
  ProductSolution,
  ProjectJobRoleFragment,
  SuccessProfileSoftSkillNameAndCategoryFragment,
  SuccessProfileTechnicalSkillNameFragment,
  TechnicalSkillFragment,
  useProjectJobRoleFindManyQuery,
} from 'generated/graphql';
import {
  isV1CognitiveSkillsVersion,
  isV2CognitiveSkillsVersion,
  TCognitiveSkillsVersion,
} from 'helpers/cognitiveSkills';
import { sortProjectJobRoles } from 'helpers/projectJobRole';
import {
  getCognitiveSoftSkillVersion,
  getMissingCategorySkills,
  isCategorySkill,
  productSolutionToCognitiveModeMap,
  sortSoftSkillsByCategory,
  TMissingCategorySkillsInRoles,
  TSkillsByCategory,
} from 'helpers/softSkills';
import { sortBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TSkillOverview } from 'views/Project/components/ProjectJobRolesOverviewSkillsList/ProjectJobRolesOverviewSkillsList.types';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

const emptySkills = {
  softSkills: [] as TSkillOverview[],
  technicalSkills: [] as TSkillOverview[],
  missingCategorySkillsInRoles: [] as TMissingCategorySkillsInRoles,
};

const defaultErrorMessage =
  'Oops, some issue happened while getting Role hiring roles';

export const useSkillsFromProjectJobRoles = ({
  projectId,
  fetchPolicy = 'cache-and-network',
  isMainRole = undefined,
}: {
  projectId: number;
  fetchPolicy?: 'cache-and-network' | 'cache-first';
  isMainRole?: boolean;
}) => {
  const [
    hasRoleWithOnlyOneCognitiveSkill,
    setHasRoleWithOnlyOneCognitiveSkill,
  ] = useState(false);
  const { handleMsgType } = useNotification();

  const getProjectJobRoleActiveSkillsProfile = useCallback(
    (projectJobRole: ProjectJobRoleFragment) => {
      return projectJobRole.jobRole.skillsProfiles.find(
        (sp) => sp.id === projectJobRole.skillsProfileId,
      );
    },
    [],
  );

  const getOverviewSoftSkills = useCallback(
    (
      successProfileSoftSkills?:
        | SuccessProfileSoftSkillNameAndCategoryFragment[]
        | null,
    ) => {
      return (
        successProfileSoftSkills?.map((spSoftSkill) => ({
          id: spSoftSkill.softSkillId,
          name: spSoftSkill.name,
          category: spSoftSkill.softSkill.category,
        })) || []
      );
    },
    [],
  );

  const getOverviewTechnicalSkills = useCallback(
    (
      successProfileTechnicalSkills?:
        | SuccessProfileTechnicalSkillNameFragment[]
        | null,
    ) => {
      return (
        successProfileTechnicalSkills
          ?.filter(
            (
              spTechnicalSkill,
            ): spTechnicalSkill is SuccessProfileTechnicalSkillNameFragment & {
              technicalSkill: TechnicalSkillFragment;
            } => spTechnicalSkill.technicalSkill != null,
          )
          .map(({ technicalSkill }) => ({
            id: technicalSkill.id,
            name: technicalSkill.name,
          })) || []
      );
    },
    [],
  );

  const { data, error, loading } = useProjectJobRoleFindManyQuery({
    variables: { id: projectId, isMainRole: isMainRole },
    fetchPolicy,
    onError: (error) => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message || defaultErrorMessage,
      });
    },
  });

  const projectJobRoles = useMemo(
    () =>
      data?.ProjectJobRoleFindMany
        ? sortProjectJobRoles(data?.ProjectJobRoleFindMany)
        : [],
    [data?.ProjectJobRoleFindMany],
  );

  const getUniqueSkills = useCallback((skills: TSkillOverview[]) => {
    const uniqueSkills = uniqBy(skills, (skill) => skill.id);
    return sortBy(uniqueSkills, (skill) => skill.name.toLocaleLowerCase());
  }, []);

  const {
    softSkills: jobRolesSoftSkillsOverview,
    technicalSkills: jobRolesTechnicalSkillsOverview,
    missingCategorySkillsInRoles,
  } = useMemo(() => {
    if (!projectJobRoles.length) {
      return emptySkills;
    }

    const softSkills: TSkillOverview[] = [];
    const technicalSkills: TSkillOverview[] = [];
    let rolesWithOnlyOneCognitiveSkill = 0;
    let missingCategorySkillsInRoles: TMissingCategorySkillsInRoles = [];
    for (const projectJobRole of projectJobRoles) {
      const skillsProfile =
        getProjectJobRoleActiveSkillsProfile(projectJobRole);

      if (!skillsProfile) continue;

      const { successProfileSoftSkills, successProfileTechnicalSkills } =
        skillsProfile;

      const overviewSoftSkills = getOverviewSoftSkills(
        successProfileSoftSkills,
      );

      /*
       * This check is done here because we need to know if the project has at least one role with only one cognitive skill
       * In the case of Multiple roles, if even just one of those roles has only one cognitive skill
       * (either v1 or v2, it doesn't matter which version), then the cognitive mode MUST be in-depth
       * and the ability to change the cognitive mode is disabled.
       *
       * Examples:
       * - Role with 1 v2 cognitive skill + Role with 0 cognitive skills → in-depth and cannot edit
       * - Role with 1 v2 cognitive skill + Role with 2 v2 cognitive skills → in-depth and cannot edit
       * - Role with 2 v2 cognitive skills + Role with 2 v2 cognitive skills → as per product solution
       * - Role with 1 v2 cognitive skill + Role with 1 v1 cognitive skills → impossible, we do not allow
       *   a project to have roles with different cognitive skills versions
       */
      if (
        overviewSoftSkills.filter(
          (skill) =>
            isV1CognitiveSkillsVersion(
              getCognitiveSoftSkillVersion(skill.name, skill.category),
            ) ||
            (isV2CognitiveSkillsVersion(
              getCognitiveSoftSkillVersion(skill.name, skill.category),
            ) &&
              !isCategorySkill(skill)),
        ).length === 1
      ) {
        rolesWithOnlyOneCognitiveSkill = rolesWithOnlyOneCognitiveSkill + 1;
      }

      const missingCategorySkills = getMissingCategorySkills(
        overviewSoftSkills,
        projectJobRole.jobRole.name,
      );
      if (missingCategorySkills) {
        missingCategorySkillsInRoles.push(missingCategorySkills);
      }

      softSkills.push(...overviewSoftSkills);
      technicalSkills.push(
        ...getOverviewTechnicalSkills(successProfileTechnicalSkills),
      );
    }

    setHasRoleWithOnlyOneCognitiveSkill(rolesWithOnlyOneCognitiveSkill >= 1);

    return { softSkills, technicalSkills, missingCategorySkillsInRoles };
  }, [
    projectJobRoles,
    getProjectJobRoleActiveSkillsProfile,
    getOverviewSoftSkills,
    getOverviewTechnicalSkills,
  ]);

  /**
   * Skills should be unique and sorted alphabetically.
   */
  const parsedSoftSkills = useMemo(
    () => getUniqueSkills(jobRolesSoftSkillsOverview),
    [jobRolesSoftSkillsOverview, getUniqueSkills],
  );
  const parsedTechnicalSkills = useMemo(
    () => getUniqueSkills(jobRolesTechnicalSkillsOverview),
    [jobRolesTechnicalSkillsOverview, getUniqueSkills],
  );

  const softSkillsByCategory = useMemo(() => {
    return sortSoftSkillsByCategory(parsedSoftSkills);
  }, [parsedSoftSkills]);

  return {
    projectJobRoles,
    isMultiMatchProject: projectJobRoles.length > 1,
    projectJobRolesError: error,
    isLoading: loading,
    parsedSoftSkills,
    parsedTechnicalSkills,
    softSkillsByCategory,
    hasRoleWithOnlyOneCognitiveSkill,
    productSolution: projectJobRoles[0]?.project?.productSolution,
    missingCategorySkillsInRoles,
  };
};

export const useCognitiveSkillsFromRoles = (
  softSkillsByCategory: TSkillsByCategory,
  productSolution?: ProductSolution | null,
  hasRoleWithOnlyOneCognitiveSkill?: boolean,
) => {
  const [cognitiveSkillsVersion, setCognitiveSkillsVersion] =
    useState<TCognitiveSkillsVersion>(null);

  useEffect(() => {
    // v1 cognitive skills are the ones that are not in the cognitive summary category, they will be found in the right for role skills. They have names that must not be edited
    const hasV1Cognitive = softSkillsByCategory.rightForRoleSkills.some(
      (skill) =>
        isV1CognitiveSkillsVersion(
          getCognitiveSoftSkillVersion(skill.name, skill.category),
        ),
    );

    if (hasV1Cognitive) {
      setCognitiveSkillsVersion('v1');
    } else if (softSkillsByCategory.cognitiveSummarySkills.length > 0) {
      setCognitiveSkillsVersion('v2');
    } else {
      setCognitiveSkillsVersion(null);
    }
  }, [softSkillsByCategory]);

  const prefillCognitiveMode = useMemo(() => {
    // If there is at least one role with only one cognitive skill, then the cognitive mode is in-depth by default
    // If the cognitive version is v1, then the cognitive mode is in-depth regardless of the product solution
    if (
      hasRoleWithOnlyOneCognitiveSkill ||
      isV1CognitiveSkillsVersion(cognitiveSkillsVersion)
    ) {
      return CognitiveMode.InDepth;
    }

    return productSolutionToCognitiveModeMap.STREAMLINED.includes(
      productSolution || ProductSolution.ProfessionalHiring,
    )
      ? CognitiveMode.Streamlined
      : CognitiveMode.InDepth;
  }, [
    productSolution,
    hasRoleWithOnlyOneCognitiveSkill,
    cognitiveSkillsVersion,
  ]);

  return {
    cognitiveSkillsVersion,
    prefillCognitiveMode,
  };
};
